import React from "react";
import { atom, useRecoilValue, useSetRecoilState } from "recoil";
import { useLocation, useHistory } from "react-router-dom";
import { GKAlertContext } from "@granular/gds-ux-components";

import { OperationsDataEnvelope } from "../../../../models/operations";
import { getRegexMatchingFromUrl } from "../../../../helpers";
import { getCoreTranslationsFor } from "../../../../i18n";
import { WILDCARD_OPERATION_ID, ADD_OPERATION } from "../../../../constants";
import { setOperationHeader } from "../../../../services/granularAxios";
import operationsService from "../../../../services/operations";
import { getMaybeRecoilValue } from "../../../../services/recoil/nexus";

import { useLocale } from "../../locale";
import { useMaybeUserData } from "../../user";

import { usePreOperationsData } from "../preOperations";
import { useMaybeFabricFeatureFlag } from "../../featureFlags";

// Do NOT export atoms.
// This allows us to limit usage of these to only the hooks we export below.

const currentOperationIdAtom = atom<string | undefined>({
  key: "[@fabric/core] currentOperationIdAtom",
  default: undefined,
});

export const currentOperationIdAccessError = new Error(
  "Attempted to access currentOperationId but it is not available",
);

const getIsOperationIdInFullOperationsList = async (
  operationId: string,
  operationsDataEnvelope: OperationsDataEnvelope,
  catalogFlag?: boolean,
) => {
  const { operations, operationsById, totalOperationsCount } =
    operationsDataEnvelope;

  if (operationId) {
    const operationFromOperationsById = operationsById[operationId];
    if (operationFromOperationsById) {
      return true;
    } else {
      // If the operation is not in operationsById, it may be
      // because we limit the fetch count to a reasonable size.
      // If we don't have all the operations fetched,
      // check to see if the op is available in the full list.
      const doWeHaveAllOperations = operations.length === totalOperationsCount;
      if (!doWeHaveAllOperations) {
        // TODO: We should be using `refetch` and `enabled`
        // of React Query on useOperationQuery in order to execute this query
        // within the context of useOperationQuery so it can be cached and de-duped.
        // However, this only affects super users and has a minimal effect
        // on page load speed so I am saving this work for a rainy day.
        try {
          await operationsService.getOperationResponse(
            operationId,
            catalogFlag,
          );
          return true;
        } catch {
          // Do nothing. Continue to final return statement.
        }
      }
    }
  }
  return false;
};

const getFallbackOperationId = async (
  operationsDataEnvelope: OperationsDataEnvelope,
  catalogFlag?: boolean,
): Promise<string> => {
  const { operations } = operationsDataEnvelope;
  // We will use the local storage as the default
  // and fallback to the first operation available.
  const localStorageOperationId = localStorage.getItem(
    operationsService.CURRENT_OPERATION_KEY_IN_LOCAL_STORAGE,
  );
  if (localStorageOperationId) {
    const isLocalStorageOperationIdInFullOperationsList =
      await getIsOperationIdInFullOperationsList(
        localStorageOperationId,
        operationsDataEnvelope,
        catalogFlag,
      );
    if (isLocalStorageOperationIdInFullOperationsList) {
      return localStorageOperationId;
    }
  }
  return operations[0].id;
};

let promiseResolvesFromUseClearCurrentOperationId: Array<() => void> = [];

/**
 * Manage the currentOperationId.
 *
 * There should be ONE and ONLY ONE instance of this hook live at a time.
 * Preferably high up in the component hierarchy.
 */
export const useManageCurrentOperationId = ({
  translateRoutes,
}: {
  translateRoutes?: boolean;
}): void => {
  const currentOperationId = useRecoilValue(currentOperationIdAtom);
  const _setCurrentOperationId = useSetRecoilState(currentOperationIdAtom);
  const catalogFlag = useMaybeFabricFeatureFlag({
    key: "roles-api",
  });

  const setCurrentOperationId = React.useCallback(
    (operationId: string | undefined) => {
      console.debug(
        "[@fabric/core][useManageCurrentOperationId] setCurrentOperationId",
        operationId,
      );

      /**
       * Update the headers in the singleton Granular HTTP Client so that
       * all future requests use this operation's ID as the `x-operation` header.
       */
      setOperationHeader({ id: operationId });

      if (operationId) {
        /**
         * Remember the Current Operation ID in Local Storage so we can load
         * it on bootstrap. Worth noting again that any valid operation ID
         * specified in the URL takes precedence over this value!
         */
        localStorage.setItem(
          operationsService.CURRENT_OPERATION_KEY_IN_LOCAL_STORAGE,
          operationId,
        );

        /**
         * Resolve any promises from useClearCurrentOperationId that have
         * been waiting on the currentOperationId to be set.
         */
        promiseResolvesFromUseClearCurrentOperationId.forEach((resolve) => {
          resolve();
        });
        promiseResolvesFromUseClearCurrentOperationId = [];
      }

      /**
       * Actually save the current operation id in a reactive way.
       */
      _setCurrentOperationId(operationId);
    },
    [_setCurrentOperationId],
  );

  const user = useMaybeUserData();

  const operationsDataEnvelope = usePreOperationsData();

  const history = useHistory();
  const locale = useLocale();
  const alertCtx = React.useContext(GKAlertContext);
  const messages = getCoreTranslationsFor(locale);

  const paths = useLocation().pathname.split(
    getRegexMatchingFromUrl(
      translateRoutes ? messages.routing_operations : "operations",
    ),
  );
  const basePath = paths[0];
  const urlOperationId = paths[1];
  const appPath = paths[2];

  React.useEffect(() => {
    const performOperationIdManagement = async () => {
      if (
        !currentOperationId ||
        (urlOperationId && currentOperationId !== urlOperationId)
      ) {
        const ensureOperation = async () => {
          if (!operationsDataEnvelope) {
            return;
          }
          const isUrlOperationIdInFullOperationsList =
            await getIsOperationIdInFullOperationsList(
              urlOperationId,
              operationsDataEnvelope,
              catalogFlag,
            );

          if (isUrlOperationIdInFullOperationsList) {
            replaceUrl(urlOperationId, false);
          } else {
            replaceUrl(
              await getFallbackOperationId(operationsDataEnvelope, catalogFlag),
              true,
            );
          }
        };

        const replaceUrl = (operationId: string, notify: boolean) => {
          const actualPaths = window.location.pathname.split(
            getRegexMatchingFromUrl(
              /**
               * This so we can localize the URI
               */
              translateRoutes ? messages.routing_operations : "operations",
            ),
          );
          const actualUrlOperationId = actualPaths[1];

          // FIXME: This shouldn't happen but it does. Adding this console debug statement to prove it.
          // Because this happens, we need to check the actualUrlOperationId over the urlOperationId
          // before doing the history.replace below.
          if (actualUrlOperationId !== urlOperationId) {
            console.debug(
              "[@fabric/core][useManageCurrentOperationId] operationId in window.location and useLocation() do not match",
              { actualUrlOperationId, urlOperationId },
            );
          }

          setCurrentOperationId(operationId);

          if (operationId !== actualUrlOperationId) {
            const firstChild = appPath?.split("/")?.[1];
            const operationChild = firstChild ? `/${firstChild}` : "";
            history.replace(
              `${basePath}/operations/${operationId}${operationChild}`,
            );
            if (notify) {
              alertCtx.warning(messages.deep_link_operation_error_message, {
                global: true,
              });
            }
          }
        };

        if (!currentOperationId) {
          // First time
          if (!urlOperationId || urlOperationId === ADD_OPERATION) {
            if (!operationsDataEnvelope) {
              return;
            }
            setCurrentOperationId(
              await getFallbackOperationId(operationsDataEnvelope, catalogFlag),
            );
          } else {
            if (urlOperationId === WILDCARD_OPERATION_ID) {
              if (!operationsDataEnvelope) {
                return;
              }
              replaceUrl(
                await getFallbackOperationId(
                  operationsDataEnvelope,
                  catalogFlag,
                ),
                false,
              );
            } else {
              ensureOperation();
            }
          }
        } else {
          // Route change
          if (
            urlOperationId === WILDCARD_OPERATION_ID ||
            urlOperationId === ADD_OPERATION
          ) {
            if (urlOperationId === WILDCARD_OPERATION_ID) {
              replaceUrl(currentOperationId, false);
            }
          } else {
            ensureOperation();
          }
        }
      }
    };
    performOperationIdManagement();
  }, [
    setCurrentOperationId,
    translateRoutes,
    currentOperationId,
    operationsDataEnvelope,
    user,
    urlOperationId,
    basePath,
    appPath,
    history,
    alertCtx,
    messages,
    catalogFlag,
  ]);
};

// ################################
// ## useMaybeCurrentOperationId ##
// ################################

export type ResultOfUseMaybeCurrentOperationId = string | undefined;
export type UseMaybeCurrentOperationId =
  () => ResultOfUseMaybeCurrentOperationId;

/**
 * The returned value of this hook will contain
 * the current operation id, which may be undefined.
 *
 * Use this over the operation id from `useCurrentOperation` as this hook
 * will not trigger a re-render for you unless the operation id and only the operation id changes.
 */
export const useMaybeCurrentOperationId: UseMaybeCurrentOperationId = () => {
  return useRecoilValue(currentOperationIdAtom);
};

// ###########################
// ## useCurrentOperationId ##
// ###########################

export type ResultOfUseCurrentOperationId = string;
export type UseCurrentOperationId = () => ResultOfUseCurrentOperationId;

/**
 * The returned value of this hook will contain the current operation id.
 *
 * This hook is guaranteed to contain the operation id or will throw.
 *
 * Use this over the operation id from `useCurrentOperation` as this hook
 * will not trigger a re-render for you unless the operation id and only the operation id changes.
 */
export const useCurrentOperationId: UseCurrentOperationId = () => {
  const currentOperationId = useMaybeCurrentOperationId();
  if (currentOperationId === undefined) {
    throw currentOperationIdAccessError;
  }
  return currentOperationId;
};

// ################################
// ## useClearCurrentOperationId ##
// ################################

export type ResultOfUseClearCurrentOperationId = () => Promise<void>;
export type UseClearCurrentOperationId =
  () => ResultOfUseClearCurrentOperationId;

/**
 * The returned value of this hook will clear the current operation id
 * and resolve when the currentOperationId has been set.
 *
 * A new current operation id will be determined by useManageCurrentOperationId.
 */
export const useClearCurrentOperationId: UseClearCurrentOperationId = () => {
  const setCurrentOperationId = useSetRecoilState(currentOperationIdAtom);
  return React.useCallback(() => {
    return new Promise((resolve) => {
      setCurrentOperationId(undefined);
      promiseResolvesFromUseClearCurrentOperationId.push(resolve);
    });
  }, [setCurrentOperationId]);
};

// ###########################
// ## getCurrentOperationId ##
// ###########################

/**
 * Get the current operation id, which may be undefined.
 *
 * Be aware that because this is not a hook, this is not reactive.
 */
export const getCurrentOperationId = (): string | undefined => {
  return getMaybeRecoilValue(currentOperationIdAtom);
};
